import { routes } from "../site";
import type { Pricing } from "../../lib/pricing";

/**
 * Fiyatlandırma sayfası (/fiyatlar). Kaynak: "zhesap-fiyatlar.html" ve
 * Zhesa Dark Landing tasarımı.
 *
 * Fiyatlar ve hesaplayıcı ayarları admin panelinden yönetilir (içerik backend'i,
 * `/v1/public/pricing`); `pricing` CMS'e ulaşılamadığında kullanılan yedektir ve aynı
 * biçimdedir (bkz. lib/pricing.ts). Hesap kuralları lib/pricing.ts'te.
 *
 * Fiyatlar YILLIK girilir; kartta görünen aylık tutar yıllık / 12'dir.
 * Yalnızca yıllık lisans satılır; aylık abonelik seçeneği yoktur.
 * Hediye kapasitenin üstüne eklenen sipariş yıllık ücrete eklenir.
 *
 * AI yanıt: AI'lı paketlerde (`hasAi`) SINIRSIZDIR; sayaç ve ek ücret yoktur, bu yüzden veri
 * biçiminde AI kapasite alanı yok. Temel özelliklerin "i" balonu açıklaması da veriden gelir
 * (`coreFeatures[].description`); sayfada ayrı bir başlık eşlemesi tutulmaz.
 */
const fallbackPricing: Pricing = {
  orderStep: 500,
  /** Bu adet ve üstü kurumsal teklif; seçilebilecek en yüksek kapasite 14.500. */
  enterpriseThreshold: 15000,
  /** Üst paket aynı hacimde en fazla bu yüzde kadar pahalıysa öneri kutusu çıkar. */
  nudgePercent: 20,
  /** Starter kartındaki liste; `description` kartta "i" balonu olur. */
  coreFeatures: [
    {
      title: "Finans & Mutabakat",
      description:
        "Alınması gereken hakedişi hesaplar, pazaryeri ödemesiyle karşılaştırır. Kargo faturasını ve desiyi anlaşmayla doğrular.",
    },
    {
      title: "Kârlılık Analizi",
      description:
        "Sipariş, ürün, marka, kategori ve satış kanalı bazında gerçek net kâr. Çok satanı değil, çok kazandıranı gösterir.",
    },
    {
      title: "Kampanya Kârlılığı & Sihirbazı",
      description:
        "Girmeden önce net kârı simüle eder; Kampanya Sihirbazı kârlı Trendyol kampanyalarına otomatik katılır.",
    },
    {
      title: "Akıllı Uyarı Sistemi",
      description:
        "Kâr düşüşünü, hatayı ve fırsatı anında bildirir. Günlük, haftalık, aylık özet rapor panele girmeden hazır.",
    },
    {
      title: "Stok Zekâsı",
      description:
        "Tükenmek üzere, kritik ve hareketsiz ürünleri bulur; rafta bekleyen parayı görünür kılar.",
    },
    {
      title: "Tarayıcı Eklentisi",
      description:
        "Pazaryeri ekranlarında gezinirken ZHesap içgörüsünü anında, ekrandan çıkmadan gösterir.",
    },
  ],
  tiers: [
    {
      key: "starter",
      name: "Starter",
      nameSuffix: "da",
      tagline: "Finansal kontrolün tamamı.",
      yearlyPrice: 119900,
      giftOrders: 3000,
      hasAi: false,
      users: "1",
      isPopular: false,
      featuresTitle: "ZHesap’ın temel gücü",
      upgradeGain: null,
      boxes: [],
      comparisonGroup: null,
    },
    {
      key: "scale",
      name: "Scale",
      nameSuffix: "de",
      tagline: "Finansal kontrol + Yapay Zekâ.",
      yearlyPrice: 179900,
      giftOrders: 3000,
      hasAi: true,
      users: "5",
      isPopular: true,
      featuresTitle: "Temel ZHesap + Yapay Zekâ",
      upgradeGain: "Sınırsız AI Müşteri İlişkileri",
      comparisonGroup: "Yapay Zekâ",
      boxes: [
        {
          title: "AI Müşteri İlişkileri",
          badge: "Sınırsız",
          description:
            "Pazaryeri müşteri sorularını yapay zekâ ile sınırsız yanıtlayın. Ay sonunda hakkım bitti mi diye saymazsınız.",
        },
      ],
    },
    {
      key: "signature",
      name: "Signature",
      nameSuffix: "da",
      tagline: "Finansal kontrol + AI + Rekabet Zekâsı.",
      yearlyPrice: 239900,
      giftOrders: 3000,
      hasAi: true,
      users: "10",
      isPopular: false,
      featuresTitle: "Temel ZHesap + AI + Rekabet",
      upgradeGain: "Rakip ve Sektör Analizi",
      comparisonGroup: "Rekabet",
      boxes: [
        {
          title: "Rakip Analizi",
          /** "·" ile ayrılan rozet kartta iki çip olur. */
          badge: "10 rakip · 50 ürün",
          description: "Fiyat ve kampanya hareketlerini izleyin.",
        },
        {
          title: "Sektör Analizi",
          description: "Pazar ve kategori hareketlerini görün.",
        },
      ],
    },
  ],
  /**
   * Hediyenin ÜSTÜNE eklenen sipariş adedi → yıllık ek ücret (hacim indirimli). Her pakette hediye 3.000 ve en yüksek
   * seçilebilir kapasite 14.500 olduğundan tablo +11.500'de biter (backend kuralıyla aynı; CMS'teki tabloyla birebir).
   * +5.000'in üstünde her 500 sipariş +10.000 ₺.
   */
  orderAddons: [
    [500, 15000],
    [1000, 27500],
    [1500, 39500],
    [2000, 51000],
    [2500, 62500],
    [3000, 73500],
    [3500, 84000],
    [4000, 94500],
    [4500, 105000],
    [5000, 115000],
    [5500, 125000],
    [6000, 135000],
    [6500, 145000],
    [7000, 155000],
    [7500, 165000],
    [8000, 175000],
    [8500, 185000],
    [9000, 195000],
    [9500, 205000],
    [10000, 215000],
    [10500, 225000],
    [11000, 235000],
    [11500, 245000],
  ].map(([extraOrders, yearlyPrice]) => ({ extraOrders, yearlyPrice })),
  /**
   * "Tüm özellikleri karşılaştır" tablosunun paket verisinden türetilemeyen satırları; değerler paket sırasında.
   * Temel özellikler, paket kutuları ve kullanıcı sayısı tabloya kendiliğinden girer.
   */
  comparisonRows: [
    { group: "Hesap", label: "Ek kullanıcı", values: ["Tanımlanabilir", "Tanımlanabilir", "Tanımlanabilir"] },
    { group: "Hesap", label: "Pazaryeri ve mağaza", values: ["Sınırsız", "Sınırsız", "Sınırsız"] },
    { group: "Hesap", label: "ERP entegrasyonu", values: ["1", "1", "1"] },
  ],
};

/**
 * "AI yanıt gerçekten sınırsız mı?" hem kartların altındaki kutuda hem SSS listesinde durur;
 * tek kaynaktan okunsun diye ayrı tanımlı.
 */
const aiUnlimitedFaq = {
  q: "AI yanıt gerçekten sınırsız mı?",
  a: "Evet. Scale ve Signature paketlerinde “aylık 1.000, 3.000, 5.000 yanıta kadar” gibi bir sınır yoktur. Mağazanıza gelen her müşteri sorusu yapay zekâ ile yanıtlanır; ay sonunda hakkım bitti mi diye saymazsınız. <br/> Olağan iş kullanımının belirgin şekilde üzerinde, otomatik ya da kötüye kullanım niteliğinde bir yoğunluk olursa sizinle görüşür, ihtiyacınıza uygun bir yapı öneririz.",
} as const;

export const pricingPage = {
  href: routes.fiyatlar,
  hero: {
    /** Sabit kısım; devamı harf harf yazılıp silinir (`phrases`, ilk cümle ön-çizimde durur). */
    title: "Fiyatımız net.",
    phrases: [
      "Kargo faturan da öyle mi?",
      "Pazaryeri hakedişin de öyle mi?",
      "Kampanya kârın da öyle mi?",
      "Müşteri yanıtların da öyle mi?",
      "Rakip takibin de öyle mi?",
    ],
    lead: "Her pakette kargo ve pazaryeri faturaların, anlaşmanla sipariş sipariş karşılaştırılır.",
  },
  currency: "₺",
  pricing: fallbackPricing,
  /** Tek sipariş seçici: üç kart birlikte değişir. */
  selector: {
    label: "Ayda kaç sipariş?",
    minus: "500 azalt",
    plus: "500 artır",
    slider: "Aylık sipariş sayısı",
    /** İlk çip en düşük hediyedir ("3.000'e kadar"), son çip kurumsal eşik ("15.000+"); aradakiler buradan. */
    presets: [5000, 7500, 10000],
    upTo: (orders: string) => `${orders}'e kadar`,
    plusSuffix: "+",
  },
  /**
   * Seçici ile kartlar arasındaki şerit; `**` kalın vurgudur (RichText). "AI" yerine "yapay zekâ". Kapsam açıkça
   * PAZARYERİ soruları: WhatsApp gibi kanallar yok, ziyaretçi öyle anlamasın (örnek pazaryeri listesi yazılmaz).
   */
  aiBanner: {
    badge: "Yeni",
    text: "Scale ve Signature’da pazaryerlerinden gelen müşteri sorularına yapay zekâ yanıtı artık **sınırsız**. Kullanım kotası yok, ay sonunda tükenme sorunu yok.",
    /** Telefonda gösterilen kısa hâli: uzun metin orada üç satırlık bir kutu oluyordu. */
    short: "Scale ve Signature’da pazaryeri sorularına yapay zekâ yanıtı artık **sınırsız**.",
  },
  /** Kartların altındaki açıklama kutusu (SSS'teki soruyla aynı metin). */
  aiNote: aiUnlimitedFaq,
  card: {
    popular: "En çok tercih edilen",
    perYearlyMonth: "/ aylık",
    yearlySub: (yearly: string) => `${yearly} / yıl · fiyat 12 ay sabit`,
    enterprisePrice: "Kurumsal teklif",
    enterpriseSub: (threshold: string) => `Ayda ${threshold}+ sipariş, hacmine özel fiyat`,
    /** Üst paketlerin modül bölümündeki "bir alttaki paketin her şeyi" kutusu. */
    includes: {
      first: (name: string, suffix: string) => `${name}’${suffix}ki tüm modüller`,
      rest: (name: string, suffix: string) => `${name}’${suffix}ki her şey`,
      modules: (name: string) => `${name} modülleri`,
    },
    users: (users: string) => `${users} kullanıcı`,
    extraUsers: "(+ ek kullanıcı eklenebilir)",
    stores: "Sınırsız mağaza",
    cta: { label: "7 Gün Ücretsiz Dene →", href: "/#erken-erisim" },
    enterpriseCta: { label: "Kurumsal Teklif Al →", href: "/#erken-erisim" },
  },
  compare: {
    title: "Tüm özellikleri karşılaştır",
    /** Kullanıcı sayısı satırı paket alanından gelir; grup adı CMS'teki ek satırlarla ("Hesap") birleşir. */
    usersGroup: "Hesap",
    usersLabel: "Kullanıcı",
    yes: "Dahil",
    no: "Yok",
  },
  /** Gerçek vaka bandı: tutarlar sabit; paket çubukları fiyat verisinden gelir. */
  proof: {
    eyebrow: "Gerçek vaka",
    title: "Bir müşterimiz 2 ayda 495.618 ₺ fazla kesilen kargo faturası buldu.",
    desc: "27.089 gönderiyi anlaşmasıyla tek tek karşılaştırdık. Fark kargo firmasından tazmin edildi.",
    cta: { label: "Son 3 ayını ücretsiz kontrol edelim →", href: "/#erken-erisim" },
    amount: 495618,
    amountLabel: "Fazla kesilen kargo faturası (2 ay)",
    tierLabel: (name: string) => `${name}, yıllık`,
    note: "Vaka müşteri izniyle, isim verilmeden paylaşılıyor. Her firmanın sonucu farklıdır.",
  },
  enterprise: {
    title: (threshold: string) => `Aylık ${threshold}+ sipariş mi işliyorsun?`,
    desc: "Hacmine özel kurumsal fiyat ve kurulum planı hazırlayalım.",
    cta: { label: "Kurumsal Teklif Al", href: "/#erken-erisim" },
    vat: "Fiyatlara KDV dahildir. Yıllık lisans, fiyat 12 ay sabit.",
  },
  faq: {
    eyebrow: "SSS",
    title: "Sık sorulan sorular",
    items: [
      {
        q: "Sipariş analizi kapasitesi nedir?",
        a: "ZHesap’ın bir ay içinde kârlılık, hakediş ve maliyet analizini yaptığı sipariş sayısıdır. Her pakette belirli bir sipariş analizi kapasitesi hediye olarak gelir; ihtiyacın daha fazlaysa paketini değiştirmeden kapasiteni artırabilirsin.",
      },
      {
        q: "Paketimdeki sipariş analizi kapasitesini artırabilir miyim?",
        a: "Evet. Paketinizi değiştirmeden +500, +1.000, +2.500 veya +5.000 sipariş analizi kapasitesi ekleyebilirsiniz. Aylık 15.000 siparişin üzerindeki hacimler için size özel teklif hazırlarız.",
      },
      {
        q: "Ay içinde sipariş hakkım biterse ne olur?",
        a: "Panelden tek seferlik ek sipariş hakkı satın alabilirsin. Ek hak, satın aldığın tarihten itibaren 30 gün geçerlidir; ayın sonunda silinmez. İstersen otomatik kapasite korumasını açarak hakkın bittiğinde sistemin otomatik yükleme yapmasını sağlayabilirsin.",
      },
      {
        q: "Kullanmadığım sipariş hakkı sonraki aya devreder mi?",
        a: "Hayır. Paketindeki sipariş analizi hakkı her ay, lisansının başlangıç tarihine denk gelen günde otomatik olarak yenilenir.",
      },
      {
        q: "Sınırsız pazaryeri ve mağaza ne anlama geliyor?",
        a: "Sınırsız kanal entegrasyonu, aynı tüzel kişiliğe / satıcı hesabına bağlı mağazalar için geçerlidir. Farklı tüzel kişiliklere ait mağazalar için ayrı paket gerekir.",
      },
      aiUnlimitedFaq,
      {
        q: "Paketimi daha sonra yükseltebilir miyim?",
        a: "Evet, dilediğin zaman. Yükseltmede yalnızca sözleşmenin kalan süresine düşen fark gün bazında hesaplanır ve yeni kapasiten hemen tanımlanır. Paket ya da kapasite düşürme ise yıllık yenileme döneminde yapılır.",
      },
      {
        q: "ZHesap yıllık mı ödeniyor?",
        a: "Evet. ZHesap paketleri yıllık lisansla sunulur. Fiyatlandırma sayfasında gördüğün aylık tutar, yıllık lisans bedelinin aylık karşılığıdır. Fiyatın 12 ay boyunca sabit kalır; yıl içinde zam görmezsin. Lisans süren dolduğunda güncel fiyat üzerinden 12 ay daha yenilenir; yenilenmesini istemezsen bitişten en az 15 gün önce haber vermen yeterli.",
      },
      {
        q: "Fiyatlara KDV dahil mi?",
        a: "ZHesap, Teknoloji Geliştirme Bölgesi (Teknopark) bünyesinde geliştirilen bir yazılım platformudur. Sunulan yazılım hizmetleri, yürürlükteki mevzuat çerçevesinde 3065 sayılı KDV Kanunu’nun Geçici 20/1. maddesi kapsamında KDV’den istisnadır. Bu nedenle belirtilen fiyatlara ayrıca KDV eklenmez. Faturanızda istisna kapsamı belirtilerek, gördüğünüz tutar üzerinden düzenlenir.",
      },
      {
        q: "Ücretsiz deneyebilir miyim?",
        a: "Evet. Demo görüşmesinden sonra ZHesap’ı kendi mağaza verilerinle 7 gün ücretsiz deneyebilirsin.",
      },
      {
        q: "Aylık 15.000’den fazla sipariş işliyorum, hangi paket bana uygun?",
        a: "Üç paketin her biri 15.000+ sipariş için de kullanılabilir. Hacmine ve ihtiyaç duyduğun modüllere göre sana özel kurumsal teklif hazırlıyoruz.",
      },
    ],
  },
} as const;
